import { Component, Inject, OnInit, CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';

import { SHARED_IMPORTS } from '../shared/shared-imports';
import { ContentService } from '../../services/content.service';
import { ConfirmDialogComponent } from '../shared/confirm-dialog/confirm-dialog.component';
import { InlineEditorComponent } from '../shared/inline-editor/inline-editor.component';
import { OrderByPipe } from '../../pipes/order-by.pipe';

@Component({
  selector: 'location-base-data-component',
  standalone: true,
  templateUrl: 'components/location-base-data/location-base-data.component.html',
  imports: [
    ...SHARED_IMPORTS,
    MatDialogModule,
    MatIconModule,
    ConfirmDialogComponent,
    InlineEditorComponent,
    OrderByPipe,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA],
})
export class LocationBaseDataComponent implements OnInit {

  private router = inject(Router);
  private content = inject(ContentService);
  private http = inject(HttpClient);
  private dialog = inject(MatDialog);
  
  public locationStructure: any[] = [];
  public siteStructure: any[] = [];
  public tempSiteStructure: any[] = [];
  public siteList: any[] = [];
  public newSite: any = null;
  public siteLevelActive = false;
  public permissions: any = {};
  public withNavbar = true;
  readMode = false;
  editingId: string | null = null;
  movingId: string | null = null;
  moveOptions: any[] = [];





  startEdit(position: any, evt?: Event, type?: string): void {
    if (evt) evt.stopPropagation();
    if (this.readMode) return;
    this.editingId = position?.Id ?? null;
    if (type) {
      this.editingId = this.editingId ? this.editingId + type : type;
    }
  }

  ngOnInit(): void {
    this.getFinalLocationOverview();
  }

  onInlineConfirm(newName: any, node: any, nodePropName: string) {
    this.editingId = null;
    node[nodePropName] = newName;
    this.saveArea(newName, node.Id);
  }

  cancelEdit() { this.editingId = null; }

  public getContent = (id: string): string => this.content.getContent(id);

  private getLocationAreas(): void {
    this.http.get('/api/Location/GetAllLocationAreas')
      .subscribe((response: any) => {
        this.locationStructure = Array.isArray(response) ? response : (response?.data ?? []);
      });
  }

  private getSites(): void {
    this.http.get('/api/Location/GetAllSites')
      .subscribe((response: any) => {
        const data = Array.isArray(response) ? response : (response?.data ?? []);
        this.siteStructure = data;
        this.tempSiteStructure = data;
      });
  }

  private getFinalLocationOverview(): void {
    this.http.get('/api/Tenant/HasSiteLevel')
      .subscribe((hasSite: any) => {
        this.siteLevelActive = !!hasSite;
        if (this.siteLevelActive) {
          this.getSites();
        } else {
          this.getLocationAreas();
        }
      });
  }

  public goBack(): void {
    this.router.navigate(['/Main']);
  }

  public addNewLocation(locationName: string): void {
    this.editingId = null;
    if (!locationName?.trim()) return;
    this.http.post(`/api/location/AddArea?locationString=${encodeURIComponent(locationName)}`, {})
      .subscribe((result: any) => {
        const id = result?.data ?? result;
        if (id) {
          this.locationStructure = [...this.locationStructure, { Id: id, LocationString: locationName }];
        }
      });
  }

  public addNewSite(siteName: string): void {
    this.editingId = null;
    if (!siteName?.trim()) return;
    this.http.post(`/api/location/AddSite?siteString=${encodeURIComponent(siteName)}`, {})
      .subscribe((result: any) => {
        const id = result?.data ?? result;
        if (id) {
          this.siteStructure = [...this.siteStructure, { Id: id, SiteString: siteName }];
          this.tempSiteStructure = this.siteStructure;
        }
      });
  }



  onMoveClick(site: any, location: any, ev: MouseEvent): void {
    ev.stopPropagation();
    // compute once
    this.moveOptions = (this.siteStructure || []).filter(s => s?.Id !== site?.Id);
    this.newSite = this.moveOptions[0] ?? null;
    this.movingId = location.Id; // shows the editor
  }

  onMoveConfirm(newSite: any, location: any, currentSite: any): void {
    this.movingId = null;
    if (!newSite) return;
    this.moveAreaToAnotherSite(newSite.Id, location, currentSite);
  }

  public addNewLocationWithSite(locationName: string, site: any): void {
    this.editingId = null;
    if (!locationName?.trim() || !site?.Id) return;
    this.http.post(
      `/api/location/AddAreaWithSite?locationString=${encodeURIComponent(locationName)}&siteId=${encodeURIComponent(site.Id)}`,
      {}
    ).subscribe((result: any) => {
      const id = result?.data ?? result;
      if (id) {
        const updatedSite = { ...site, LocationAreas: [...(site.LocationAreas ?? []), { Id: id, LocationString: locationName }] };
        this.siteStructure = this.siteStructure.map(s => s.Id === updatedSite.Id ? updatedSite : s);
        this.tempSiteStructure = this.siteStructure;
      }
    });
  }

  public addNewRoom(roomName: string, location: any): void {
    this.editingId = null;
    if (!roomName?.trim() || !location?.Id) return;
    this.http.post(`/api/location/AddRoom?locationId=${encodeURIComponent(location.Id)}&roomName=${encodeURIComponent(roomName)}`, {})
      .subscribe((result: any) => {
        const id = result?.data ?? result;
        if (id) {
          const updated = {
            ...location,
            Rooms: [...(location.Rooms ?? []), { Id: id, RoomName: roomName }]
          };
          this.patchLocationInTrees(updated);
        }
      });
  }

  public addNewPosition(positionName: string, room: any): void {
    this.editingId = null;
    if (!positionName?.trim() || !room?.Id) return;
    this.http.post(`/api/location/AddPosition?roomId=${encodeURIComponent(room.Id)}&positionName=${encodeURIComponent(positionName)}`, {})
      .subscribe((result: any) => {
        const id = result?.data ?? result;
        if (id) {
          const updated = { ...room, Positions: [...(room.Positions ?? []), { Id: id, PositionName: positionName }] };
          this.patchRoomInTrees(updated);
        }
      });
  }

  public savePosition(newName: string, position: any): void {
    this.editingId = null;
    if (!newName?.trim() || !position.Id) return;
    this.http.post(`/api/location/RenamePosition?positionId=${position.Id}&newName=${encodeURIComponent(newName)}`, {})
      .subscribe(() => {
        const updated = { ...position, PositionName: newName };
        this.patchPositionInTrees(updated);
      });
  }

  public saveRoom(newName: string, room: any): void {
    this.editingId = null;
    if (!newName?.trim() || !room.Id) return;
    this.http.post(`/api/location/RenameRoom?roomId=${room.Id}&newName=${encodeURIComponent(newName)}`, {})
      .subscribe(() => {
        const updated = { ...room, RoomName: newName };
        this.patchRoomInTrees(updated);
      });
  }

  public saveArea(newName: string, area: any): void {
    this.editingId = null;
    if (!newName?.trim() || !area.Id) return;
    this.http.post(`/api/location/RenameArea?areaId=${area.Id}&newName=${encodeURIComponent(newName)}`, {})
      .subscribe(() => {
        const updated = { ...area, LocationString: newName };
        this.patchLocationInTrees(updated);
      });
  }

  public saveSite(newName: string, site: any): void {
    this.editingId = null;
    if (!newName?.trim() || !site.Id) return;
    this.http.post(`/api/location/RenameSite?siteId=${site.Id}&newName=${encodeURIComponent(newName)}`, {})
      .subscribe(() => {
        const updated = { ...site, SiteString: newName };
        this.patchSiteInTrees(updated);
      });
  }

  public deletePosition(room: any, position: any): void {
    const ref = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.content.getContent('LocationBaseDataEdit.DeleteThisPosition'),
        text: this.content.getContent('LocationBaseDataEdit.ConfirmDeletePositionText'),
        okText: this.content.getContent('Standard.Delete'),
        cancelText: this.content.getContent('Standard.Cancel'),
      }
    });

    ref.afterClosed().subscribe((confirmed) => {
      if (!confirmed) return;
      this.http.post(`/api/location/DeletePosition?positionId=${position.Id}`, {})
        .subscribe((result: any) => {
          if ((result?.status ?? 200) === 200) {
            const updated = { ...room, Positions: (room.Positions ?? []).filter((p: any) => p !== position) };
            this.patchRoomInTrees(updated);
          }
        });
    });
  }

  public deleteRoom(location: any, room: any): void {
    if (room?.Positions?.length) {
      this.dialog.open(ConfirmDialogComponent, {
        data: {
          title: this.content.getContent('LocationBaseData.DeleteAllPositionsFirstTitle'),
          text: this.content.getContent('LocationBaseData.DeleteAllPositionsFirstText'),
          okText: this.content.getContent('Standard.Ok'),
          cancelText: ''
        }
      });
      return;
    }

    const ref = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.content.getContent('LocationBaseData.ConfirmDeleteRoomTitle'),
        text: this.content.getContent('LocationBaseData.ConfirmDeleteRoomText'),
        okText: this.content.getContent('Standard.Delete'),
        cancelText: this.content.getContent('Standard.Cancel'),
      }
    });

    ref.afterClosed().subscribe((confirmed) => {
      if (!confirmed) return;
      this.http.post(`/api/location/DeleteRoom?roomId=${room.Id}`, {})
        .subscribe(() => {
          const updated = { ...location, Rooms: (location.Rooms ?? []).filter((r: any) => r !== room) };
          this.patchLocationInTrees(updated);
        });
    });
  }

  public deleteArea(site: any, area: any): void {
    if (area?.Rooms?.length) {
      this.dialog.open(ConfirmDialogComponent, {
        data: {
          title: this.content.getContent('LocationBaseData.DeleteAllRoomsFirstTitle'),
          text: this.content.getContent('LocationBaseData.DeleteAllRoomsFirstText'),
          okText: this.content.getContent('Standard.Ok'),
          cancelText: ''
        }
      });
      return;
    }

    const ref = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.content.getContent('LocationBaseData.ConfirmDeleteLocationTitle'),
        text: this.content.getContent('LocationBaseData.ConfirmDeleteLocationText'),
        okText: this.content.getContent('Standard.Delete'),
        cancelText: this.content.getContent('Standard.Cancel'),
      }
    });

    ref.afterClosed().subscribe((confirmed) => {
      if (!confirmed) return;
      this.http.post(`/api/location/DeleteArea?areaId=${area.Id}`, {})
        .subscribe(() => {
          if (this.siteLevelActive) {
            const updatedSite = {
              ...site,
              LocationAreas: (site.LocationAreas ?? []).filter((a: any) => a !== area)
            };
            this.siteStructure = this.siteStructure.map(s => s.Id === updatedSite.Id ? updatedSite : s);
            this.tempSiteStructure = this.siteStructure;
          } else {
            this.locationStructure = this.locationStructure.filter(a => a !== area);
          }
          this.permissions = {};
        });
    });
  }

  public getSiteStructure(site: any): any[] {
    this.siteList = this.siteStructure.filter((s) => s !== site);
    this.newSite = this.siteList[0]?.Id ?? null;
    return this.siteList;
  }

  expandedSiteIds = new Set<string>();

  isExpanded(id: string): boolean {
    return this.expandedSiteIds.has(String(id));
  }

  switchArrow(id: string): void {
    const key = String(id);
    if (this.expandedSiteIds.has(key)) this.expandedSiteIds.delete(key);
    else this.expandedSiteIds.add(key);
  }

  // Angular methods for click handlers
  triggerEdit(item: any, event?: Event, type?: string): void {
    if (event) {
      event.stopPropagation();
    }
    this.startEdit(item, event, type);
  }


  public moveAreaToAnotherSite(newSiteId: any, location: any, currentSite: any): void {
    const ref = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.content.getContent('LocationBaseDataEdit.MoveThisArea'),
        text: this.content.getContent('LocationBaseDataEdit.ConfirmMoveThisAreaText'),
        okText: this.content.getContent('Standard.Move'),
        cancelText: this.content.getContent('Standard.Cancel'),
      }
    });

    ref.afterClosed().subscribe((confirmed) => {
      if (!confirmed) return;
      this.http.post(
        `/api/location/MoveAreaToAnotherSite?locationId=${encodeURIComponent(location.Id)}&siteId=${encodeURIComponent(newSiteId)}`, {}
      ).subscribe(() => {
        // Remove from old site
        const updatedOld = {
          ...currentSite,
          LocationAreas: (currentSite.LocationAreas ?? []).filter((la: any) => la.Id !== location.Id)
        };
        // Add to new site
        const target = this.siteStructure.find(s => s.Id === newSiteId);
        if (target) {
          const updatedTarget = {
            ...target,
            LocationAreas: [...(target.LocationAreas ?? []), { ...location, SiteId: newSiteId }]
          };
          this.siteStructure = this.siteStructure.map(s =>
            s.Id === updatedOld.Id ? updatedOld :
              s.Id === updatedTarget.Id ? updatedTarget : s
          );
        } else {
          // if the new site isn't in the list for some reason, just patch old
          this.siteStructure = this.siteStructure.map(s => s.Id === updatedOld.Id ? updatedOld : s);
        }
        this.tempSiteStructure = this.siteStructure;
      });
    });
  }

  public deleteSite(site: any): void {
    if (site?.LocationAreas?.length) {
      this.dialog.open(ConfirmDialogComponent, {
        data: {
          title: this.content.getContent('LocationBaseData.DeleteAllLocationAreasFirstTitle'),
          text: this.content.getContent('LocationBaseData.DeleteAllLocationAreasFirstText'),
          okText: this.content.getContent('Standard.Ok'),
          cancelText: ''
        }
      });
      return;
    }

    const ref = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.content.getContent('LocationBaseData.ConfirmDeleteSiteTitle'),
        text: this.content.getContent('LocationBaseData.ConfirmDeleteSiteText'),
        okText: this.content.getContent('Standard.Delete'),
        cancelText: this.content.getContent('Standard.Cancel'),
      }
    });

    ref.afterClosed().subscribe((confirmed) => {
      if (!confirmed) return;
      this.http.post(`/api/location/DeleteSite?siteId=${site.Id}`, {})
        .subscribe(() => {
          this.siteStructure = this.siteStructure.filter(s => s !== site);
          this.tempSiteStructure = this.siteStructure;
          this.permissions = {};
        });
    });
  }

  private patchSiteInTrees(updatedSite: any): void {
    if (!this.siteLevelActive || !updatedSite?.Id) return;

    let changed = false;

    this.siteStructure = this.siteStructure.map(site => {
      if (site?.Id !== updatedSite.Id) return site;
      const next: any = {
        ...site,
        ...updatedSite,
        LocationAreas: (updatedSite.LocationAreas ?? site.LocationAreas)
      };
      if (next !== site) changed = true;
      return next;
    });

    if (changed) {
      this.tempSiteStructure = this.siteStructure;
    }
  }

  private patchLocationInTrees(updatedLocation: any): void {
    if (this.siteLevelActive) {
      this.siteStructure = this.siteStructure.map(site => {
        if (!site.LocationAreas) return site;
        const idx = site.LocationAreas.findIndex((a: any) => a.Id === updatedLocation.Id);
        if (idx === -1) return site;
        const nextAreas = [...site.LocationAreas];
        nextAreas[idx] = updatedLocation;
        return { ...site, LocationAreas: nextAreas };
      });
      this.tempSiteStructure = this.siteStructure;
    } else {
      this.locationStructure = this.locationStructure.map(a => a.Id === updatedLocation.Id ? updatedLocation : a);
    }
  }

  private patchRoomInTrees(updatedRoom: any): void {
    const patchRooms = (rooms: any[]) => rooms.map(r => r.Id === updatedRoom.Id ? updatedRoom : r);
    if (this.siteLevelActive) {
      this.siteStructure = this.siteStructure.map(site => {
        if (!site.LocationAreas) return site;
        const areas = site.LocationAreas.map((area: any) => {
          if (!area.Rooms) return area;
          return { ...area, Rooms: patchRooms(area.Rooms) };
        });
        return { ...site, LocationAreas: areas };
      });
      this.tempSiteStructure = this.siteStructure;
    } else {
      this.locationStructure = this.locationStructure.map(area => {
        if (!area.Rooms) return area;
        return { ...area, Rooms: patchRooms(area.Rooms) };
      });
    }
  }

  private patchPositionInTrees(updatedPosition: any): void {
    const replaceInPositions = (positions: any[] | undefined) =>
      (positions ?? []).map(p => (p?.Id === updatedPosition.Id ? updatedPosition : p));

    if (this.siteLevelActive) {
      this.siteStructure = this.siteStructure.map(site => {
        if (!site?.LocationAreas) return site;

        const nextAreas = site.LocationAreas.map((area: any) => {
          if (!area?.Rooms) return area;

          const nextRooms = area.Rooms.map((room: any) => {
            if (!room?.Positions) return room;
            const nextPositions = replaceInPositions(room.Positions);

            if (nextPositions === room.Positions ||
              (nextPositions.length === room.Positions.length &&
                nextPositions.every((p, i) => p === room.Positions[i]))) {
              return room;
            }
            return { ...room, Positions: nextPositions };
          });

          if (nextRooms === area.Rooms ||
            (nextRooms.length === area.Rooms.length &&
              nextRooms.every((r, i) => r === area.Rooms[i]))) {
            return area;
          }
          return { ...area, Rooms: nextRooms };
        });

        if (nextAreas === site.LocationAreas ||
          (nextAreas.length === site.LocationAreas.length &&
            nextAreas.every((a, i) => a === site.LocationAreas[i]))) {
          return site;
        }
        return { ...site, LocationAreas: nextAreas };
      });

      this.tempSiteStructure = this.siteStructure;
    } else {
      this.locationStructure = this.locationStructure.map(area => {
        if (!area?.Rooms) return area;

        const nextRooms = area.Rooms.map((room: any) => {
          if (!room?.Positions) return room;
          const nextPositions = replaceInPositions(room.Positions);

          if (nextPositions === room.Positions ||
            (nextPositions.length === room.Positions.length &&
              nextPositions.every((p, i) => p === room.Positions[i]))) {
            return room;
          }
          return { ...room, Positions: nextPositions };
        });

        if (nextRooms === area.Rooms ||
          (nextRooms.length === area.Rooms.length &&
            nextRooms.every((r, i) => r === area.Rooms[i]))) {
          return area;
        }
        return { ...area, Rooms: nextRooms };
      });
    }
  }
}
